import React, {useContext, useMemo} from 'react';
import * as PropTypes from 'prop-types';
import styles from './styles.modules.scss';
import CheckBold from 'ati-ui-react/components/icons/CheckBold';
import TimerSolid from 'ati-ui-react/components/icons/TimerSolid';

import Link from 'ati-ui-react/components/Link';
import { getContrastColor } from 'as-ui-kit/utils/boards';
import {participantTypes} from '../consts';
import {useLocalized} from 'utils/useLocalized';
import i18nStrings from './strings';
import interpolate from 'ati-utils/interpolate';
import {mainSiteUrl} from 'urls';
import Tooltip from '@components/tooltip';
import {isUsersParticipateOrInvited} from '../utils';
import Checkbox from '@components/checkbox';
import pluralize from 'ati-utils/pluralize';
import {firmsAccusativeCasePlural} from 'stringsPlural';
import {LocaleContext} from 'contexts';

const BoardRow = ({board, onBoardToggle, atiIds}) => {
    const strings = useLocalized(i18nStrings);
    const locale = useContext(LocaleContext);
    const isDisabled = !board.availableForContact || isUsersParticipateOrInvited(board, atiIds);
    const disabledTooltipText = !board.availableForContact ? strings.lackOfRights
	    : atiIds.length > 1 ? strings.selectedFirmsAreApplicants : strings.selectedFirmAreApplicants;

    const applicationsOrParticipantIdsSet = new Set(board.applicationsOrParticipantIds);
    const addedToBoardFirmsCount = atiIds.filter(id => applicationsOrParticipantIdsSet.has(id)).length;
    const notAddedToBoardFirmsCount =  atiIds.length - addedToBoardFirmsCount;

    const iconJsx = useMemo(()=>{
        switch (board.participantType) {
            case participantTypes.user:
                return (
                    <CheckBold
                        height={12}
                        width={18}
                    />
                );
            case participantTypes.invited:
            case participantTypes.invitedViewed:
                return (
                    <TimerSolid
                        height={12}
                        width={12}
                    />
                );
            default:
                return '';
        }

    }, [board]);

    const status = useMemo(()=>{
        switch (board.participantType) {
            case participantTypes.user:
                return strings.participant;
            case participantTypes.invited:
            case participantTypes.invitedViewed:
                return strings.invited;
            default: return '';
        }
    }, [board, strings]);

    const checkbox = (
        <Checkbox
            checked={board.checked}
            disabled={isDisabled}
            onChange={isDisabled ? undefined : (event => onBoardToggle(event, board.id))}
        />
    );

    return (
        <div
            className={styles['board-row']}
        >
	        <div className={styles['board-info']}>
		        <div
                    className={styles['board']}
                    onClick={isDisabled ? undefined : (event => onBoardToggle(event, board.id))}
		        >
	            {isDisabled ? (
                        <Tooltip
                            content={disabledTooltipText}
                            maxWidth={160}
                        >
                            {checkbox}
                        </Tooltip>
	            ) : checkbox}
                    <div
                        className={styles['board-name']}
                        style={{backgroundColor: board.color, color: getContrastColor(board.color)}}
                    >
                        {board.name}
                    </div>
                </div>
                <div className={styles['participant-info']}>
                    <div className={styles['status-description']}>
                        {iconJsx}
                        {status}
                    </div>
                    <Link
                        gray
                        href={`${mainSiteUrl}/boards/board/${board.id}`}
                        target="_blank"
                        type="external"
                    >
                        <span>
                            {board.participantsCount>0? interpolate(strings.participantCount, {count: board.participantsCount}): strings.empty}
                        </span>
                    </Link>
                </div>
	        </div>
	        {(!!addedToBoardFirmsCount && !isUsersParticipateOrInvited(board, atiIds) && board.checked) && (
		        <div className={styles['firms-invite-warning-row']}>
			        <div>
				        {interpolate(pluralize(strings.invitedFirmsCount, addedToBoardFirmsCount, locale), {
					        count: addedToBoardFirmsCount,
				        })}
			        </div>
			        {interpolate(strings.firmsToBeInvitedCount, {
				        count: notAddedToBoardFirmsCount,
				        firmPlural: pluralize(firmsAccusativeCasePlural[locale], notAddedToBoardFirmsCount, locale)
			        })}
		        </div>
	        )}
        </div>
    );
};

export default BoardRow;

BoardRow.propTypes = {
    atiIds: PropTypes.arrayOf(PropTypes.string),
    board: PropTypes.object,
    onBoardToggle: PropTypes.func,
};
